import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import '../styles/Admin.scss';
import { FiMenu, FiList, FiDatabase, FiHome, FiDownload } from "react-icons/fi"; // react-icons 활용

interface Report {
  reportId: string;
  cloggingLevel: string;
  causeType: string;
  phoneNumber: string;
  latitude: number;
  longitude: number;
  createdAt: string;
  photoUrl: string;
  status: string;
}

interface FloodDamage {
  _id: string;
  sequence: number;
  address: string;
  damageDate: string;
  latitude: number;
  longitude: number;
}

interface ApiResponse<T> {
  reports?: T[];
  floodDamages?: T[];
  currentPage: number;
  totalPages: number;
  totalReports?: number;
  totalDocs?: number;
}

const AdminPage: React.FC = () => {
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null);
  const [activeTab, setActiveTab] = useState<'reports' | 'flood'>('reports');
  const [reports, setReports] = useState<Report[]>([]);
  const [floodDamages, setFloodDamages] = useState<FloodDamage[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  
  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const LIMIT = 20;

  // 검색 및 필터 상태
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [cloggingFilter, setCloggingFilter] = useState<string>('');
  const [causeFilter, setCauseFilter] = useState<string>('');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    const checkPermission = async () => {
      try {
        await axios.get(`${apiUrl}/api/admin/check-ip`);
        setIsAllowed(true);
      } catch (error) {
        setIsAllowed(false);
      }
    };
    checkPermission();
  }, [apiUrl]);

  // 탭 변경 시 초기화
  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setStatusFilter('');
    setCloggingFilter('');
    setCauseFilter('');
  }, [activeTab]);

  useEffect(() => {
    if (!isAllowed) return;

    const fetchData = async () => {
      setLoadingData(true);
      try {
        const params: any = { 
            page: currentPage, 
            limit: LIMIT,
            search: searchTerm
        };

        // 날짜 필터 (공통)
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;

        if (activeTab === 'reports') {
            if (statusFilter) params.status = statusFilter;
            if (cloggingFilter) params.cloggingLevel = cloggingFilter;
            if (causeFilter) params.causeType = causeFilter;

            const res = await axios.get<ApiResponse<Report>>(`${apiUrl}/api/reports/list`, { params });
            setReports(res.data.reports || []);
            setTotalPages(res.data.totalPages);
            setTotalCount(res.data.totalReports || 0);
        } else {
            const res = await axios.get<ApiResponse<FloodDamage>>(`${apiUrl}/api/flood-damages/list`, { params });
            setFloodDamages(res.data.floodDamages || []);
            setTotalPages(res.data.totalPages);
            setTotalCount(res.data.totalDocs || 0);
        }
      } catch (err) {
        console.error("데이터 로딩 실패:", err);
      } finally {
        setLoadingData(false);
      }
    };

    const timer = setTimeout(() => {
        fetchData();
    }, 300);

    return () => clearTimeout(timer);
  }, [isAllowed, activeTab, currentPage, apiUrl, searchTerm, startDate, endDate, statusFilter, cloggingFilter, causeFilter]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
          &lt;
        </button>
        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => handlePageChange(number)}
            className={currentPage === number ? 'active' : ''}
          >
            {number}
          </button>
        ))}
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
          &gt;
        </button>
      </div>
    );
  };

  const handleDownloadExcel = async () => {
    try {
        const params: any = { 
            page: 1, 
            limit: 10000, 
            search: searchTerm
        };

        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;

        let dataToExport = [];
        let fileName = '';

        if (activeTab === 'reports') {
            if (statusFilter) params.status = statusFilter;
            if (cloggingFilter) params.cloggingLevel = cloggingFilter;
            if (causeFilter) params.causeType = causeFilter;

            const res = await axios.get<ApiResponse<Report>>(`${apiUrl}/api/reports/list`, { params });
            const reports = res.data.reports || [];
            
            dataToExport = reports.map(r => ({
                '상태': r.status,
                '신고일시': new Date(r.createdAt).toLocaleString(),
                '막힘정도': r.cloggingLevel,
                '원인': r.causeType,
                '전화번호': r.phoneNumber,
                '위치(위도)': r.latitude,
                '위치(경도)': r.longitude,
                '사진URL': r.photoUrl ? `${apiUrl}${r.photoUrl}` : ''
            }));
            fileName = `신고내역_${new Date().toISOString().slice(0,10)}.xlsx`;

        } else {
            const res = await axios.get<ApiResponse<FloodDamage>>(`${apiUrl}/api/flood-damages/list`, { params });
            const floodDamages = res.data.floodDamages || [];

            dataToExport = floodDamages.map(f => ({
                '연번': f.sequence,
                '주소': f.address,
                '피해발생일자': f.damageDate,
                '위도': f.latitude,
                '경도': f.longitude
            }));
            fileName = `침수데이터_${new Date().toISOString().slice(0,10)}.xlsx`;
        }

        if (dataToExport.length === 0) {
            alert('다운로드할 데이터가 없습니다.');
            return;
        }

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Data");
        XLSX.writeFile(wb, fileName);

    } catch (err) {
        console.error("엑셀 다운로드 실패:", err);
        alert("데이터 다운로드 중 오류가 발생했습니다.");
    }
  };

  const renderFilters = () => (
    <div className="filter-bar" style={{ display: 'flex', gap: '10px', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input 
            type="text" 
            placeholder={activeTab === 'reports' ? "전화번호 검색" : "주소 검색"} 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        />
        
        {/* 공통 날짜 필터 */}
        <input 
            type="date" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        />
        <span style={{ alignSelf: 'center' }}>~</span>
        <input 
            type="date" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)}
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        />

        {activeTab === 'reports' && (
            <>
                <select 
                    value={statusFilter} 
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                    <option value="">모든 상태</option>
                    <option value="접수">접수</option>
                    <option value="처리중">처리중</option>
                    <option value="완료">완료</option>
                </select>
                <select 
                    value={cloggingFilter} 
                    onChange={(e) => setCloggingFilter(e.target.value)}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                    <option value="">모든 막힘정도</option>
                    <option value="양호">양호</option>
                    <option value="주의">주의</option>
                    <option value="막힘">막힘</option>
                    <option value="폐색">폐색</option>
                </select>
                <select 
                    value={causeFilter} 
                    onChange={(e) => setCauseFilter(e.target.value)}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                    <option value="">모든 원인</option>
                    <option value="안막힘">안막힘</option>
                    <option value="쓰레기">쓰레기</option>
                    <option value="담배꽁초">담배꽁초</option>
                    <option value="낙엽">낙엽</option>
                    <option value="시멘트">시멘트</option>
                    <option value="나뭇가지">나뭇가지</option>
                    <option value="폐기물">폐기물</option>
                    <option value="토사">토사</option>
                    <option value="기타">기타</option>
                </select>
            </>
        )}
        
        <button 
            onClick={handleDownloadExcel}
            className="action-btn"
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: '#28a745', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer' }}
        >
            <FiDownload /> 엑셀 다운로드
        </button>
    </div>
  );

  if (isAllowed === null) return <div className="loading-screen">권한 확인 중...</div>;
  if (!isAllowed) return (
    <div className="error-screen">
        <h1 style={{color:'red', marginBottom:'1rem'}}>접근 불가</h1>
        <p>관리자 페이지에 접근할 수 있는 권한이 없습니다.</p>
        <p>허용된 IP가 아닙니다.</p>
    </div>
  );

  return (
    <div className="admin-container">
      {/* 사이드바 */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
            <a href="/adminpage">ADMIN</a>
        </div>
        <ul className="sidebar-menu">
            <li>
                <button 
                    className={activeTab === 'reports' ? 'active' : ''} 
                    onClick={() => setActiveTab('reports')}
                >
                    <FiList /> 신고 관리
                </button>
            </li>
            <li>
                <button 
                    className={activeTab === 'flood' ? 'active' : ''} 
                    onClick={() => setActiveTab('flood')}
                >
                    <FiDatabase /> 침수 데이터
                </button>
            </li>
            <li>
                <a href="/">
                    <FiHome /> 메인으로 돌아가기
                </a>
            </li>
        </ul>
        <div className="sidebar-footer">
            &copy; 2026 Drainage Gutter
        </div>
      </aside>

      {/* 메인 컨텐츠 */}
      <main className="admin-content">
        <div className="content-header">
            <h1>{activeTab === 'reports' ? '신고 내역 관리' : '침수 피해 데이터 관리'}</h1>
        </div>

        <div className="card">
            <div className="card-header">
                <h3>데이터 목록</h3>
                <span className="total-count">총 {totalCount} 건</span>
            </div>
            
            <div style={{ padding: '0 1.5rem' }}>
                {renderFilters()}
            </div>

            {loadingData ? (
                <div className="loading-data">데이터를 불러오는 중입니다...</div>
            ) : (
                <>
                <div className="table-responsive">
                    {activeTab === 'reports' ? (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>상태</th>
                                    <th>신고일시</th>
                                    <th>막힘정도</th>
                                    <th>원인</th>
                                    <th>전화번호</th>
                                    <th>위치</th>
                                    <th>사진</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.length === 0 ? (
                                    <tr><td colSpan={8} style={{textAlign:'center'}}>데이터가 없습니다.</td></tr>
                                ) : reports.map((report) => (
                                    <tr key={report.reportId}>
                                        <td>
                                            <span className={`status-badge ${report.status}`}>
                                                {report.status}
                                            </span>
                                        </td>
                                        <td>{new Date(report.createdAt).toLocaleString()}</td>
                                        <td>{report.cloggingLevel}</td>
                                        <td>{report.causeType}</td>
                                        <td>{report.phoneNumber}</td>
                                        <td>{report.latitude.toFixed(5)}, {report.longitude.toFixed(5)}</td>
                                        <td>
                                            {report.photoUrl && (
                                                <a href={`${apiUrl}${report.photoUrl}`} target="_blank" rel="noreferrer" className="link-btn">
                                                    [보기]
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>연번</th>
                                    <th>주소</th>
                                    <th>피해발생일자</th>
                                    <th>위도</th>
                                    <th>경도</th>
                                </tr>
                            </thead>
                            <tbody>
                                {floodDamages.length === 0 ? (
                                    <tr><td colSpan={5} style={{textAlign:'center'}}>데이터가 없습니다.</td></tr>
                                ) : floodDamages.map((data) => (
                                    <tr key={data._id || data.sequence}>
                                        <td>{data.sequence}</td>
                                        <td>{data.address}</td>
                                        <td>{data.damageDate}</td>
                                        <td>{data.latitude}</td>
                                        <td>{data.longitude}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                {renderPagination()}
                </>
            )}
        </div>
      </main>
    </div>
  );
};

export default AdminPage;